import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch, Typography, Button } from '@mui/material';
import { Card, Autocomplete, TextField } from '@totalsoft/rocket-ui';
import insuranceTypeNames from 'constants/insuranceTypeNames';
import { Details } from '@mui/icons-material';
import { get, set } from '@totalsoft/change-tracking-react';
import { emptyString } from 'constants/emptyConst';
import { connect } from 'react-redux';
import { useToast } from 'utils/hooks';
import { ownInsurance } from 'constants/ownInsurance';
import { isDirty } from '@totalsoft/change-tracking';
import { getErrors, isValid } from '@totalsoft/pure-validations';
import { ParamsContext } from 'providers/ParamsContext';
import OfferType from 'constants/offerType';
import documentTypeIds from 'constants/documentTypeIds';
import { durationCondition } from 'constants/rtiBehaviour';
import { RightsContext } from 'providers/RightsContext';

const GeneralInsuranceDataCard = props => {
  const { lens, onSave, dirtyInfo, saving, validation, offerTypeId, documentTypeId, userIsDealer } = props;

  const { t } = useTranslation();
  const addToast = useToast();
  const { readOnlyFull, isImage, offerInsuranceGeneralDataWrite } = useContext(RightsContext);
  const { selfPartnerShortName } = useContext(ParamsContext);

  const [showOwnInsuranceInfo, setShowOwnInsuranceInfo] = useState(false);

  // #region Memos

  const disabled = useMemo(() => readOnlyFull || !offerInsuranceGeneralDataWrite, [readOnlyFull, offerInsuranceGeneralDataWrite]);
  const ownInsurancePossibleValues = useMemo(
    () => [
      { name: t('Offer.Insurance.OwnInsurance.FirstYear'), code: ownInsurance.FirstYear },
      { name: t('Offer.Insurance.OwnInsurance.EntirePeriod'), code: ownInsurance.EntirePeriod },
      { name: selfPartnerShortName, code: ownInsurance.No },
      { name: t('Offer.Insurance.OwnInsurance.NoInsuranceOption'), code: ownInsurance.NoInsurance }
    ],
    [t, selfPartnerShortName]
  );

  const cascoOwnInsurance = useMemo(() => lens.ownCasco |> get, [lens]);
  const rcaOwnInsurance = useMemo(() => lens.ownRca |> get, [lens]);

  // #endregion

  const onFinancedInsuranceChanged = useCallback(
    (insuranceTypeName = null) =>
      event => {
        const value = event.target.checked;
        if (insuranceTypeName === insuranceTypeNames.CASCO && (lens.allowCascoFinancing |> get)) {
          (lens.financedInsuranceCasco |> set)(value);
          (lens.financedPeriodCasco |> set)(value ? lens.duration |> get : null);
        } else if (insuranceTypeName === insuranceTypeNames.RCA && (lens.allowRcaFinancing |> get)) {
          (lens.financedInsuranceRca |> set)(value);
          (lens.financedPeriodRca |> set)(value ? lens.duration |> get : null);
        } else if (insuranceTypeName === insuranceTypeNames.RTI && (lens.allowRtiFinancing |> get)) {
          (lens.financedInsuranceRti |> set)(value);
          (lens.financedPeriodRti |> set)(
            value
              ? (lens.duration |> get) > durationCondition.contractMaxDuration
                ? durationCondition.contractMaxDuration
                : lens.duration |> get
              : null
          );
        } else {
          addToast(t('Offer.Insurance.FinancedInsuranceInfo'), 'info');
          return;
        }
      },
    [t, lens, addToast]
  );

  return (
    <>
      <Prompt when={isDirty(dirtyInfo)} message={() => t('SaveChanges', { card: t('Offer.Insurance.GeneralData') })} />
      <Card icon={Details} title={t('Offer.Insurance.GeneralData')}>
        <>
          <Grid container spacing={1} justifyContent='space-between' alignItems='center'>
            {offerTypeId !== OfferType.restructuring.id && (
              <Grid item container xs={12} spacing={2} alignItems='center'>
                <Grid item xs={12} sm={2}>
                  <Typography>{t('Offer.Insurance.RCA')}</Typography>
                </Grid>
                {!userIsDealer && (
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      fullWidth
                      isSearchable={true}
                      simpleValue
                      valueKey='code'
                      labelKey='name'
                      label={t('Offer.Insurance.OwnInsurance.Label')}
                      options={ownInsurancePossibleValues}
                      onChange={code => (lens.ownRca |> set)(code)}
                      getOptionSelected={(option, value) => option.code === value.code && option.name === value.name}
                      value={(lens.ownRca |> get) || emptyString}
                      disabled={disabled || (lens.ownRcaReadOnly |> get)}
                      error={!isValid(validation.ownRca)}
                      helperText={getErrors(validation.ownRca)}
                    />
                  </Grid>
                )}
                {userIsDealer && (rcaOwnInsurance === ownInsurance.FirstYear || rcaOwnInsurance === ownInsurance.EntirePeriod) ? (
                  <Grid item xs={12} sm={3}>
                    {t('Offer.Insurance.OwnInsuranceSelected')}
                  </Grid>
                ) : (
                  userIsDealer && <Grid item xs={12} sm={3}></Grid>
                )}
                {rcaOwnInsurance !== ownInsurance.FirstYear && rcaOwnInsurance !== ownInsurance.EntirePeriod && (
                  <>
                    <Grid container item xs={12} sm={3} justifyContent='center' alignItems='center'>
                      <FormControlLabel
                        control={
                          <Switch
                            id={'financedInsurance_rca'}
                            color={'primary'}
                            size='small'
                            checked={(lens.financedInsuranceRca |> get) || false}
                            onChange={onFinancedInsuranceChanged(insuranceTypeNames.RCA)}
                            disabled={disabled}
                          />
                        }
                        label={t('Offer.Insurance.FinancedInsurance')}
                      />
                    </Grid>
                    {!userIsDealer && (
                      <Grid container item xs={12} sm={3}>
                        <TextField
                          id={'financedPeriod_rca'}
                          fullWidth
                          isNumeric
                          inputProps={{
                            decimalScale: 0
                          }}
                          label={t('Offer.Insurance.FinancedPeriod')}
                          disabled={disabled || !(lens.financedInsuranceRca |> get)}
                          value={(lens.financedPeriodRca |> get) || emptyString}
                          onChange={lens.financedPeriodRca |> set}
                          error={!isValid(validation.financedPeriodRca)}
                          helperText={getErrors(validation.financedPeriodRca)}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            )}
            {cascoOwnInsurance !== undefined && (
              <Grid item container xs={12} spacing={2} alignItems='center'>
                <Grid item xs={12} sm={2}>
                  <Typography>{t('Offer.Insurance.CASCO')}</Typography>
                </Grid>
                {!userIsDealer && (
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      fullWidth
                      isSearchable={true}
                      simpleValue
                      valueKey='code'
                      labelKey='name'
                      label={t('Offer.Insurance.OwnInsurance.Label')}
                      options={ownInsurancePossibleValues}
                      onChange={code => (lens.ownCasco |> set)(code)}
                      getOptionSelected={(option, value) => option.code === value.code && option.name === value.name}
                      value={(lens.ownCasco |> get) || emptyString}
                      disabled={disabled || (lens.ownCascoReadOnly |> get)}
                      error={!isValid(validation.ownCasco)}
                      helperText={getErrors(validation.ownCasco)}
                    />
                  </Grid>
                )}
                {userIsDealer && (cascoOwnInsurance === ownInsurance.FirstYear || cascoOwnInsurance === ownInsurance.EntirePeriod) ? (
                  <Grid item xs={12} sm={3}>
                    {t('Offer.Insurance.OwnInsuranceSelected')}
                  </Grid>
                ) : userIsDealer && cascoOwnInsurance === ownInsurance.NoInsurance ? (
                  <Grid item xs={12} sm={3}>
                    {t('Offer.Insurance.NoInsurance')}
                  </Grid>
                ) : (
                  userIsDealer && <Grid item xs={12} sm={3}></Grid>
                )}
                {cascoOwnInsurance === ownInsurance.No && (
                  <>
                    <Grid container item xs={12} sm={3} justifyContent='center' alignItems='center'>
                      <FormControlLabel
                        control={
                          <Switch
                            id={'financedInsurance_casco'}
                            color={'primary'}
                            size='small'
                            checked={(lens.financedInsuranceCasco |> get) || false}
                            onChange={onFinancedInsuranceChanged(insuranceTypeNames.CASCO)}
                            disabled={disabled}
                          />
                        }
                        label={t('Offer.Insurance.FinancedInsurance')}
                      />
                    </Grid>
                    {!userIsDealer && (
                      <Grid item xs={12} sm={3}>
                        <TextField
                          id={'financedPeriod_casco'}
                          fullWidth
                          isNumeric
                          inputProps={{
                            decimalScale: 0
                          }}
                          label={t('Offer.Insurance.FinancedPeriod')}
                          disabled={disabled || !(lens.financedInsuranceCasco |> get)}
                          value={(lens.financedPeriodCasco |> get) || emptyString}
                          onChange={lens.financedPeriodCasco |> set}
                          error={!isValid(validation.financedPeriodCasco)}
                          helperText={getErrors(validation.financedPeriodCasco)}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            )}
            {(documentTypeId === documentTypeIds.Offer || documentTypeId === documentTypeIds.OfferImage) &&
              cascoOwnInsurance === ownInsurance.No && (
                <Grid item container xs={12} spacing={2} alignItems='center'>
                  <Grid item xs={12} sm={2}>
                    <Typography>{t('Offer.Insurance.RTI')}</Typography>
                  </Grid>
                  {<Grid item xs={12} sm={3}></Grid>}
                  <Grid container item xs={12} sm={3} justifyContent='center' alignItems='center'>
                    <FormControlLabel
                      control={
                        <Switch
                          id={'financedInsurance_rti'}
                          color={'primary'}
                          size='small'
                          checked={(lens.financedInsuranceRti |> get) || false}
                          onChange={onFinancedInsuranceChanged(insuranceTypeNames.RTI)}
                          disabled={disabled}
                        />
                      }
                      label={t('Offer.Insurance.FinancedInsurance')}
                    />
                  </Grid>
                  {!userIsDealer && (
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id={'financedPeriod_rti'}
                        fullWidth
                        isNumeric
                        inputProps={{ decimalScale: 0 }}
                        label={t('Offer.Insurance.FinancedPeriod')}
                        disabled={disabled || !(lens.financedInsuranceRti |> get)}
                        value={(lens.financedPeriodRti |> get) || emptyString}
                        onChange={lens.financedPeriodRti |> set}
                        error={!isValid(validation.financedPeriodRti)}
                        helperText={getErrors(validation.financedPeriodRti)}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            <Grid item xs={12} container justifyContent='flex-end'>
              {/*<Grid item>
                <IconButton
                  id="offer_insurance_info"
                  size="small"
                  color="theme"
                  tooltip={t("Offer.Insurance.OwnInsuranceInfo")}
                  onClick={onOpenShowInfo}
                  onClose={onCloseShowInfo}
                >
                  <HelpOutline />
                </IconButton>
              </Grid>*/}
              {!isImage && (
                <Grid item>
                  <Button color='primary' variant='contained' onClick={onSave} disabled={disabled || saving || !isDirty(dirtyInfo)}>
                    {t('Save')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Dialog open={showOwnInsuranceInfo} id={'offer_insurance_dialog'}>
            <DialogTitle>{t('Offer.Insurance.OwnInsuranceInfo')}</DialogTitle>
            <DialogContent>{t('Offer.Insurance.OwnInsuranceContent')}</DialogContent>
            <DialogActions>
              <Button onClick={() => setShowOwnInsuranceInfo(false)}>{t('Close')}</Button>
            </DialogActions>
          </Dialog>
        </>
      </Card>
    </>
  );
};

GeneralInsuranceDataCard.propTypes = {
  lens: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  dirtyInfo: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  validation: PropTypes.object.isRequired,
  offerTypeId: PropTypes.number.isRequired,
  documentTypeId: PropTypes.number.isRequired,
  userIsDealer: PropTypes.bool.isRequired
};

function mapStateToProps(state) {
  const { documentTypeId, offerTypeId } = state.currentOffer.header;
  const userIsDealer = !state.userInfo.dealerIsSelfPartner && !state.userInfo.userIsAdmin;

  return {
    documentTypeId,
    offerTypeId,
    userIsDealer
  };
}

export default connect(mapStateToProps)(GeneralInsuranceDataCard);
